import { useParams, Link } from 'react-router-dom';
import { useMemo } from 'react';
import { Layout } from '@/components/layout/Layout';
import { SEOHead } from '@/components/shared/SEOHead';
import { DocsNav } from '@/components/docs/DocsNav';
import { DocsSidebarNav, SidebarSection } from '@/components/docs/DocsSidebarNav';
import { useLookupsDoc, useLookupGroup } from '@/hooks/useLookupsDoc';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '@/components/ui/breadcrumb';
import { List, ChevronLeft, ChevronRight } from 'lucide-react';

function formatGroupName(name: string): string {
  return name
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export default function LookupGroupPage() {
  const { group: groupSlug } = useParams<{ group: string }>();
  const { data: groupData, isLoading, error } = useLookupGroup(groupSlug || '');
  const { data: allLookups } = useLookupsDoc();

  // Build sidebar sections
  const sidebarSections: SidebarSection[] = useMemo(() => {
    if (!allLookups) return [];
    
    return [{
      title: 'Lookup Groups',
      slug: 'lookups',
      items: allLookups.groups.map((g) => ({
        title: formatGroupName(g.name),
        slug: g.name,
        href: `/docs/lookups/${g.name}`,
      })),
    }];
  }, [allLookups]);

  // Find prev/next groups
  const { prevGroup, nextGroup } = useMemo(() => {
    if (!allLookups || !groupSlug) return { prevGroup: null, nextGroup: null };
    
    const groups = allLookups.groups;
    const currentIndex = groups.findIndex((g) => g.name === groupSlug);
    
    return {
      prevGroup: currentIndex > 0 ? groups[currentIndex - 1] : null,
      nextGroup: currentIndex < groups.length - 1 ? groups[currentIndex + 1] : null,
    };
  }, [allLookups, groupSlug]);

  return (
    <Layout>
      <SEOHead 
        path={`/docs/lookups/${groupSlug}`}
        title={groupData ? `${formatGroupName(groupData.name)} | Lookups | AutomotiveAPI Docs` : undefined}
        description={groupData ? `Browse ${groupData.count} lookup values for ${formatGroupName(groupData.name)}` : undefined}
      />
      
      <DocsNav />
      
      <div className="container py-8">
        <div className="flex gap-8">
          {/* Sidebar */}
          <DocsSidebarNav sections={sidebarSections} className="hidden lg:block" />
          
          {/* Main Content */}
          <main className="flex-1 min-w-0 max-w-3xl">
            {/* Breadcrumb */}
            <Breadcrumb className="mb-6">
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/docs">Docs</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/docs/lookups">Lookups</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                {groupData && (
                  <>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                      <BreadcrumbPage>{formatGroupName(groupData.name)}</BreadcrumbPage>
                    </BreadcrumbItem>
                  </>
                )}
              </BreadcrumbList>
            </Breadcrumb>
            
            {/* Loading State */}
            {isLoading && (
              <div className="space-y-4">
                <Skeleton className="h-10 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
                <div className="mt-8">
                  <Skeleton className="h-64 w-full" />
                </div>
              </div>
            )}
            
            {/* Error State */}
            {error && (
              <div className="text-center py-12">
                <List className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h2 className="text-xl font-semibold mb-2">Lookup group not found</h2>
                <p className="text-muted-foreground mb-4">
                  The lookup group you're looking for doesn't exist or has been moved.
                </p>
                <Button asChild variant="outline">
                  <Link to="/docs/lookups">Browse all lookup groups</Link>
                </Button>
              </div>
            )}
            
            {/* Group Content */}
            {groupData && (
              <>
                <header className="mb-8">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="p-3 rounded-lg bg-primary/10">
                      <List className="h-6 w-6 text-primary" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h1 className="text-3xl font-bold">{formatGroupName(groupData.name)}</h1>
                        <Badge variant="secondary">
                          {groupData.count} value{groupData.count !== 1 ? 's' : ''}
                        </Badge>
                      </div>
                      <code className="text-sm text-muted-foreground">{groupData.name}</code>
                    </div>
                  </div>
                </header>
                
                {/* Values Table */}
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-1/4">Value</TableHead>
                        <TableHead className="w-1/3">Display Name</TableHead>
                        <TableHead>Description</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {groupData.items.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell>
                            <code className="text-sm bg-muted px-1.5 py-0.5 rounded">
                              {item.value}
                            </code>
                          </TableCell>
                          <TableCell className="font-medium">
                            {item.display_name}
                          </TableCell>
                          <TableCell className="text-muted-foreground text-sm">
                            {item.description || '—'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                
                {/* Prev/Next Navigation */}
                <nav className="mt-12 pt-8 border-t flex items-center justify-between">
                  {prevGroup ? (
                    <Link 
                      to={`/docs/lookups/${prevGroup.name}`}
                      className="flex items-center gap-2 text-sm hover:text-primary transition-colors"
                    >
                      <ChevronLeft className="h-4 w-4" />
                      <div className="text-right">
                        <div className="text-muted-foreground text-xs">Previous</div>
                        <div className="font-medium">{formatGroupName(prevGroup.name)}</div>
                      </div>
                    </Link>
                  ) : (
                    <div />
                  )}
                  
                  {nextGroup && (
                    <Link 
                      to={`/docs/lookups/${nextGroup.name}`}
                      className="flex items-center gap-2 text-sm hover:text-primary transition-colors"
                    >
                      <div className="text-left">
                        <div className="text-muted-foreground text-xs">Next</div>
                        <div className="font-medium">{formatGroupName(nextGroup.name)}</div>
                      </div>
                      <ChevronRight className="h-4 w-4" />
                    </Link>
                  )}
                </nav>
              </>
            )}
          </main>
        </div>
      </div>
    </Layout>
  );
}
